import React from 'react';
import classNames from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';

import './DraggableCard.styles.scss';
import './DraggableCard.styles.responsive.scss';

type DraggableCardRenderProps = {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement) => void;
};

type DraggableCardProps = {
  className?: string;
  children?:
    | React.ReactNode
    | ((props: DraggableCardRenderProps) => React.ReactNode);
  id: string;
  disabled?: boolean;
  includeResize?: boolean;
  dragEnabled?: boolean;
};

const DraggableCard: React.FC<DraggableCardProps> = (props) => {
  const {
    id,
    children,
    className,
    disabled,
    includeResize = false,
    dragEnabled = true,
  } = props;

  const {
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
    isSorting,
    setNodeRef,
    setActivatorNodeRef,
  } = useSortable({ id, disabled });

  const hasRenderChildren = typeof children === 'function';

  const cleanTransform = includeResize
    ? transform
    : { ...transform, scaleX: 1, scaleY: 1 };

  const style = {
    ...((isDragging || isSorting) && {
      transition,
      transform: CSS.Transform.toString(cleanTransform),
    }),
  };

  const classes = classNames(
    'draggable-card',
    { 'draggable-card--dragging': isDragging },
    className,
  );

  return (
    <div
      className={classes}
      ref={setNodeRef}
      style={style}
      {...(!hasRenderChildren && attributes)}
      {...(!hasRenderChildren && dragEnabled && listeners)}
    >
      {hasRenderChildren
        ? children({
            attributes,
            listeners: dragEnabled ? listeners : undefined,
            setActivatorNodeRef,
          })
        : children}
    </div>
  );
};

export default DraggableCard;
